import React, { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./QuestionContent.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { formatReferenceExcerpt } from "../utils/referenceExcerpt";

// Helper function to remove .pdf extension from reference names
const removeFileExtension = (fileName: string): string => {
  if (!fileName) return fileName;
  return fileName.replace(/\.pdf$/i, "");
};

// Props for QuestionContent component
interface QuestionContentProps {
  questionId?: string;
  // Add props for real-time updates
  realTimeData?: {
    questionText?: string;
    specialty?: string;
    status?: string;
    answer?: string;
    justification?: string;
    references?: ReferenceItem[];
  };
  isTransitioning?: boolean;
}

// Interface for database question structure
interface DbQuestion {
  id: string;
  created_at: string;
  user_id: string;
  question: string;
  answer: {
    answer: string;
    justification: string;
    source_types_used: string[];
    excerpts_and_references: Array<{
      type: string;
      details: {
        page_number: number;
      };
      source_name: string;
      text_excerpt: string;
    }>;
  };
  specialty: string;
  tokens: string;
}

// Reference item structure
interface ReferenceItem {
  citedText: string;
  reference: string;
  pageNumber: string;
  type?: string;
  details?: {
    page_number?: number;
    authors?: string[] | string;
    journal?: string;
    date?: string;
    doi?: string;
  };
}

/**
 * QuestionContent component displays a standalone question and its answer
 * Fetches the question data from the API when a questionId is provided
 */
const QuestionContent: React.FC<QuestionContentProps> = ({
  questionId,
  realTimeData,
  isTransitioning,
}) => {
  // State to track if references dropdown is open
  const [refsOpen, setRefsOpen] = useState(false);
  // State for the question data from API
  const [questionData, setQuestionData] = useState<DbQuestion | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceData, setReferenceData] = useState<ReferenceItem[]>([]);

  // Real-time state for streaming updates
  const [isRealTimeMode, setIsRealTimeMode] = useState<boolean>(false);
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [currentQuestionText, setCurrentQuestionText] = useState<string>("");
  const [currentAnswer, setCurrentAnswer] = useState<string>("");
  const [currentJustification, setCurrentJustification] = useState<string>("");
  const [currentReferences, setCurrentReferences] = useState<ReferenceItem[]>(
    []
  );
  const [currentSpecialty, setCurrentSpecialty] = useState<string>("");

  // Fade transition state
  const [fadeClass, setFadeClass] = useState<string>(
    "question-content-fade-enter-active"
  );

  // Get user from auth context
  const { user } = useAuthContext();

  // Toggle references dropdown
  const toggleRefs = () => {
    setRefsOpen(!refsOpen);
  };

  // Effect to handle question data fetching and mode detection
  useEffect(() => {
    // Update fade class based on transition state from parent
    if (isTransitioning) {
      setFadeClass("question-content-fade-exit-active");
    } else if (questionId) {
      // Not transitioning and have questionId, show content normally with fade-in
      setFadeClass("question-content-fade-enter-active");
    }

    // Only fetch data if not transitioning
    if (questionId && !isTransitioning) {
      // If realTimeData is provided, we are in a live-streaming scenario.
      if (realTimeData) {
        setIsRealTimeMode(true);
        setIsLoading(false);
        setError(null);
        // The other useEffect handles updating the state from realTimeData.
        return;
      }

      // This is a real question ID, so fetch its data from the database.
      setIsRealTimeMode(false);

      // Set loading state
      setIsLoading(true);
      setError(null);

      // Function to fetch question data
      const fetchQuestionData = async () => {
        try {
          // Use the new specific endpoint to fetch the question by ID
          const apiUrl =
            process.env.REACT_APP_API_URL || "http://localhost:5001";

          const response = await authenticatedFetch(`${apiUrl}/api/question/${questionId}`, {
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            if (response.status === 404) {
              throw new Error("Question not found");
            }
            throw new Error(`Error fetching question: ${response.statusText}`);
          }

          const data = await response.json();

          if (data.success && data.question) {
            const question = data.question;

            setQuestionData(question);

            // Set the real-time state from database data
            setCurrentStatus("");
            setCurrentSpecialty(question.specialty || "");
            setCurrentQuestionText(question.question || "");
            setCurrentAnswer(question.answer?.answer || "");
            setCurrentJustification(question.answer?.justification || "");

            // Extract references from the question data
            if (question.answer && question.answer.excerpts_and_references) {
              const extractedRefs = question.answer.excerpts_and_references.map(
                (ref: any) => ({
                  citedText: ref.text_excerpt,
                  reference: removeFileExtension(ref.source_name),
                  pageNumber: ref.details?.page_number?.toString() || "N/A",
                  type: ref.type || "textbook",
                  details: ref.details || undefined,
                })
              );
              setReferenceData(extractedRefs);
              setCurrentReferences(extractedRefs);
            } else {
              setReferenceData([]);
              setCurrentReferences([]);
            }
          } else {
            setError("Question not found");
          }
        } catch (err) {
          console.error("Error fetching question data:", err);
          if (err instanceof Error && err.message === "Question not found") {
            setError("Question not found");
          } else {
            setError("Failed to load question data. Please try again later.");
          }
        } finally {
          setIsLoading(false);
        }
      };

      fetchQuestionData();
    }
  }, [questionId, user, realTimeData, isTransitioning]);

  // Effect to handle Server-Sent Events for real-time updates
  useEffect(() => {
    // Always try to update from realTimeData if it exists, regardless of isRealTimeMode
    // This ensures we don't miss updates due to timing issues
    if (realTimeData) {
      // Force the component into real-time mode if we have real-time data
      if (!isRealTimeMode) {
        setIsRealTimeMode(true);
      }

      // Update all fields that are defined in realTimeData
      if (realTimeData.questionText !== undefined) {
        setCurrentQuestionText(realTimeData.questionText);
      }
      if (realTimeData.specialty !== undefined) {
        setCurrentSpecialty(realTimeData.specialty);
      }
      if (realTimeData.status !== undefined) {
        setCurrentStatus(realTimeData.status);
      }
      if (realTimeData.answer !== undefined) {
        setCurrentAnswer(realTimeData.answer);
      }
      if (realTimeData.justification !== undefined) {
        setCurrentJustification(realTimeData.justification);
      }
      if (realTimeData.references !== undefined) {
        setCurrentReferences(realTimeData.references);
      }
    }
  }, [realTimeData, isRealTimeMode]); // Added isRealTimeMode to dependencies

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div
      className={`${styles.questionContent} ${
        fadeClass === "question-content-fade-enter-active"
          ? styles.questionContentFadeEnterActive
          : styles.questionContentFadeExitActive
      }`}
    >
      <div className={styles.containerHeader}>
        <div className={styles.headerLeftStatusContainer}>
          <div
            className={
              isRealTimeMode && currentStatus ? styles.statusLoader : ""
            }
          >
            {currentStatus}
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.specialtyContainer}>
            Speciality: {currentSpecialty || "Loading..."}
          </div>
        </div>
      </div>

      <div className={styles.scrollableContentWrapper}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            Loading question data...
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>{error}</div>
        ) : (
          <div className={styles.containerBody}>
            <div className={styles.questionTextContainer}>
              <div className={styles.questionText}>
                {currentQuestionText || "Question text not found"}
              </div>

              <div className={styles.dateDisplay}>
                {questionData?.created_at && (
                  <span>{formatDate(questionData.created_at)}</span>
                )}
              </div>
            </div>
            <div className={styles.questionAnswerJustificationContainer}>
              <div className={styles.questionAnswerSection}>
                <div className={styles.answerLabel}>Answer:</div>

                <div className={styles.answerContainer}>
                  {currentAnswer ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {currentAnswer}
                    </ReactMarkdown>
                  ) : (
                    "No answer available"
                  )}
                </div>
              </div>

              <div className={styles.questionJustificationSection}>
                <div className={styles.justificationLabel}>Justification:</div>
                <div className={styles.justificationContainer}>
                  {currentJustification ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {currentJustification}
                    </ReactMarkdown>
                  ) : (
                    "No justification available"
                  )}
                </div>
              </div>
            </div>
            {/* References dropdown section - only show if there are references */}
            {currentReferences.length > 0 && (
              <div className={styles.referencesContainer}>
                {/* References header - clickable to toggle dropdown */}
                <div className={styles.referencesHeader} onClick={toggleRefs}>
                  <div className={styles.referencesHeaderContent}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="10"
                      height="14"
                      viewBox="0 0 10 14"
                      fill="none"
                    >
                      <path
                        d="M8.88889 0H1.11111C0.816426 0 0.533811 0.118002 0.325437 0.328047C0.117063 0.538092 0 0.822974 0 1.12002V13.4403C4.93315e-05 13.5402 0.0266303 13.6383 0.0769846 13.7244C0.127339 13.8105 0.199632 13.8814 0.286361 13.9299C0.37309 13.9783 0.471096 14.0024 0.570207 13.9998C0.669319 13.9972 0.765924 13.9678 0.85 13.9149L5 11.3003L9.15069 13.9149C9.23476 13.9677 9.33129 13.9968 9.4303 13.9994C9.52931 14.0019 9.62719 13.9777 9.71381 13.9293C9.80043 13.8809 9.87264 13.8101 9.92295 13.7241C9.97327 13.6381 9.99987 13.5401 10 13.4403V1.12002C10 0.822974 9.88294 0.538092 9.67456 0.328047C9.46619 0.118002 9.18357 0 8.88889 0ZM8.88889 1.12002V9.07008L5.29375 6.80554C5.20545 6.74991 5.10343 6.72041 4.99931 6.72041C4.89518 6.72041 4.79316 6.74991 4.70486 6.80554L1.11111 9.06938V1.12002H8.88889ZM5.29375 10.1656C5.20545 10.11 5.10343 10.0805 4.99931 10.0805C4.89518 10.0805 4.79316 10.11 4.70486 10.1656L1.11111 12.4301V10.3903L5 7.94026L8.88889 10.3903V12.4301L5.29375 10.1656Z"
                        fill="#0F2B2A"
                      />
                    </svg>
                    {currentReferences.length} Relevant references retrieved
                  </div>

                  {/* Dropdown arrow icon that rotates when open */}
                  <div
                    className={`${styles.referencesDropdownArrow} ${
                      refsOpen
                        ? styles.referencesDropdownArrowOpen
                        : styles.referencesDropdownArrowClosed
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="7"
                      height="12"
                      viewBox="0 0 7 12"
                      fill="none"
                    >
                      <path
                        d="M6.82907 11.0684C6.88326 11.1191 6.92625 11.1792 6.95558 11.2454C6.9849 11.3116 7 11.3826 7 11.4543C7 11.5259 6.9849 11.5969 6.95558 11.6631C6.92625 11.7293 6.88326 11.7895 6.82907 11.8402C6.77488 11.8908 6.71054 11.931 6.63974 11.9585C6.56893 11.9859 6.49305 12 6.41641 12C6.33977 12 6.26388 11.9859 6.19308 11.9585C6.12227 11.931 6.05794 11.8908 6.00375 11.8402L0.171065 6.38589C0.116834 6.33523 0.0738134 6.27508 0.0444609 6.20887C0.0151083 6.14265 0 6.07168 0 6C0 5.92832 0.0151083 5.85735 0.0444609 5.79113C0.0738134 5.72492 0.116834 5.66476 0.171065 5.61411L6.00375 0.159841C6.11319 0.0574964 6.26163 -2.8531e-09 6.41641 0C6.57119 2.8531e-09 6.71962 0.0574964 6.82907 0.159841C6.93851 0.262185 7 0.400994 7 0.54573C7 0.690467 6.93851 0.829275 6.82907 0.93162L1.40832 6L6.82907 11.0684Z"
                        fill="#0F2B2A"
                      />
                    </svg>
                  </div>
                </div>

                {/* References content - shown when expanded */}
                <div
                  className={`${styles.referencesContent} ${
                    refsOpen
                      ? styles.referencesContentOpen
                      : styles.referencesContentClosed
                  }`}
                >
                  {/* Reference items with the new structure */}
                  {currentReferences.map((ref, index) => (
                    <div key={index} className={styles.referenceItem}>
                      {/* Cited Text section */}
                      <div className={styles.referenceItemCitedText}>
                        <div className={styles.citedTextLabel}>Cited Text:</div>
                        <div className={styles.citedTextContent} style={{ whiteSpace: "pre-line" }}>
                          "{formatReferenceExcerpt(ref.citedText)}"
                        </div>
                      </div>

                      {/* Reference meta section */}
                      <div className={styles.referenceItemMeta}>
                        {/* Reference section with icon */}
                        <div className={styles.referenceSource}>
                          <div className={styles.referenceSourceHeader}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              fill="var(--Colors-Primary-900, #0f2b2a)"
                              viewBox="0 0 256 256"
                              className={styles.referenceSourceIcon}
                            >
                              <path d="M208,24H72A32,32,0,0,0,40,56V224a8,8,0,0,0,8,8H192a8,8,0,0,0,0-16H56a16,16,0,0,1,16-16H208a8,8,0,0,0,8-8V32A8,8,0,0,0,208,24ZM120,40h48v72L148.79,97.6a8,8,0,0,0-9.6,0L120,112Zm80,144H72a31.82,31.82,0,0,0-16,4.29V56A16,16,0,0,1,72,40h32v88a8,8,0,0,0,12.8,6.4L144,114l27.21,20.4A8,8,0,0,0,176,136a8,8,0,0,0,8-8V40h16Z"></path>
                            </svg>
                            <div className={styles.referenceSourceLabel}>
                              Reference:
                            </div>
                          </div>

                          <div className={styles.referenceSourceText}>
                            {ref.type === "article" && ref.details?.doi ? (
                              <a
                                href={`https://doi.org/${ref.details?.doi}`}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  textDecoration: "underline",
                                  fontWeight: 600,
                                  color: "inherit",
                                }}
                              >
                                {ref.reference}
                              </a>
                            ) : (
                              <>{ref.reference}</>
                            )}
                          </div>
                        </div>

                        {ref.type === "article" ? (
                          <div className={styles.referenceArticleMeta}>
                            <div className={styles.referenceArticleRow}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                fill="var(--Colors-Primary-900, #0f2b2a)"
                                viewBox="0 0 256 256"
                                style={{ marginRight: 6 }}
                              >
                                <path d="M208,48H96a8,8,0,0,0-8,8V200a8,8,0,0,0,8,8H208a8,8,0,0,0,8-8V56A8,8,0,0,0,208,48Zm-8,144H104V64H200ZM80,56V200a8,8,0,0,1-16,0V56A8,8,0,0,1,80,56Z"></path>
                              </svg>
                              <span>{ref.details?.journal || "N/A"}</span>
                            </div>
                            <div className={styles.referenceArticleRow}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                fill="var(--Colors-Primary-900, #0f2b2a)"
                                viewBox="0 0 256 256"
                                style={{ marginRight: 6 }}
                              >
                                <path d="M128,128a44,44,0,1,0-44-44A44,44,0,0,0,128,128Zm0,16c-35.35,0-64,25.07-64,56a8,8,0,0,0,8,8H184a8,8,0,0,0,8-8C192,169.07,163.35,144,128,144Z"></path>
                              </svg>
                              <span>
                                {Array.isArray(ref.details?.authors)
                                  ? (ref.details?.authors as string[])[0] ||
                                    "N/A"
                                  : (ref.details?.authors as string) || "N/A"}
                              </span>
                            </div>
                            {/* Link row removed per UI update: title now clickable; hide separate DOI row */}
                            <div className={styles.referenceArticleRow}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                fill="var(--Colors-Primary-900, #0f2b2a)"
                                viewBox="0 0 256 256"
                                style={{ marginRight: 6 }}
                              >
                                <path d="M200,40H184V24a8,8,0,0,0-16,0V40H88V24a8,8,0,0,0-16,0V40H56A16,16,0,0,0,40,56V208a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V56A16,16,0,0,0,200,40Zm0,168H56V88H200Zm0-136H56V56H72V72a8,8,0,0,0,16,0V56h80V72a8,8,0,0,0,16,0V56h16Z"></path>
                              </svg>
                              <span>
                                {(() => {
                                  // Show year only. Prefer date_of_publication, then date.
                                  const d =
                                    (ref.details as any)?.date_of_publication ||
                                    (ref.details as any)?.date ||
                                    "";
                                  const year = d
                                    ? String(d).trim().slice(0, 4)
                                    : "";
                                  return year || "N/A";
                                })()}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.referencePageNumber}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              fill="var(--Colors-Primary-900, #0f2b2a)"
                              viewBox="0 0 256 256"
                            >
                              <path d="M184,32H72A16,16,0,0,0,56,48V224a8,8,0,0,0,12.24,6.78L128,193.43l59.77,37.35A8,8,0,0,0,200,224V48A16,16,0,0,0,184,32Zm0,177.57-51.77-32.35a8,8,0,0,0-8.48,0L72,209.57V48H184Z"></path>
                            </svg>
                            <div className={styles.referencePageLabel}>
                              Page Number:
                            </div>
                            <div className={styles.referencePageText}>
                              {ref.pageNumber}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* See Also section - currently disabled. To enable, remove the `false && (...)` wrapper below. */}
        {false && (
          <>
            {/* See Also section - simplified for now */}
            {!isLoading && !error && (
              <div className={styles.seeAlsoContainer}>
                <div className={styles.seeAlsoTitle}>See Also</div>

                {/* Simplified See Also section */}
                <div className={styles.seeAlsoContent}>
                  Related questions will appear here in the future.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QuestionContent;
